import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@mui/material';

const StarIcon = ({ 
    isFavorited = false, 
    onToggle, 
    size = 20, 
    disabled = false,
    reportId,
    isDarkTheme = false 
}) => {
    const [isAnimating, setIsAnimating] = useState(false);
    const [animationDirection, setAnimationDirection] = useState('fill'); // 'fill' or 'unfill'
    const [fillProgress, setFillProgress] = useState(isFavorited ? 100 : 0);
    const animationRef = useRef(null);
    const timeoutRef = useRef(null);

    // Handle the star click
    const handleClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (disabled || isAnimating) return;

        const newFavoritedState = !isFavorited;
        const newDirection = newFavoritedState ? 'fill' : 'unfill';
        
        setIsAnimating(true);
        setAnimationDirection(newDirection);
        
        // Start the fill animation
        startFillAnimation(newDirection);
        
        try {
            // Make the API call
            await onToggle(reportId, newFavoritedState);
            
            // If API call completes before animation, speed up the rest
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                // Speed up the remaining animation
                const remainingProgress = newDirection === 'fill' ? 100 - fillProgress : fillProgress;
                const speedUpDuration = Math.max(100, remainingProgress * 2); // Minimum 100ms
                timeoutRef.current = setTimeout(() => {
                    setFillProgress(newFavoritedState ? 100 : 0);
                    setIsAnimating(false);
                }, speedUpDuration);
            }
        } catch (error) {
            // API call failed - reverse the animation
            console.error('Failed to toggle favorite:', error);
            reverseAnimation();
        }
    };

    const startFillAnimation = (direction) => {
        const duration = 800; // 800ms animation
        const startTime = Date.now();
        const startProgress = fillProgress;
        const targetProgress = direction === 'fill' ? 100 : 0;
        
        const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Easing function for smooth animation
            const easedProgress = 1 - Math.pow(1 - progress, 3);
            const currentProgress = startProgress + (targetProgress - startProgress) * easedProgress;
            
            setFillProgress(currentProgress);
            
            if (progress < 1) {
                animationRef.current = requestAnimationFrame(animate);
            } else {
                setIsAnimating(false);
            }
        };
        
        animationRef.current = requestAnimationFrame(animate);
        
        // Set timeout as fallback
        timeoutRef.current = setTimeout(() => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            setFillProgress(targetProgress);
            setIsAnimating(false);
        }, duration + 100);
    };

    const reverseAnimation = () => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        
        const reverseDirection = animationDirection === 'fill' ? 'unfill' : 'fill';
        setAnimationDirection(reverseDirection);
        startFillAnimation(reverseDirection);
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    // Update fill progress when isFavorited prop changes externally
    useEffect(() => {
        if (!isAnimating) {
            setFillProgress(isFavorited ? 100 : 0);
        }
    }, [isFavorited, isAnimating]);

    const starColor = isDarkTheme ? '#FFD700' : '#FFA500'; // Gold color
    const strokeColor = isDarkTheme ? '#FFD700' : '#FFA500';
    const glowColor = isDarkTheme ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255, 165, 0, 0.3)';

    return (
        <Box
            sx={{
                position: 'relative',
                cursor: disabled || isAnimating ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                transition: 'transform 0.2s ease',
                '&:hover': {
                    transform: disabled ? 'none' : 'scale(1.1)',
                },
                '&:active': {
                    transform: disabled ? 'none' : 'scale(0.95)',
                }
            }}
            onClick={handleClick}
        >
            <svg
                width={size}
                height={size}
                viewBox="0 0 24 24"
                style={{
                    filter: isFavorited ? `drop-shadow(0 0 4px ${glowColor})` : 'none',
                    transition: 'filter 0.3s ease'
                }}
            >
                {/* Star outline */}
                <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    fill="none"
                    stroke={strokeColor}
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                />
                
                {/* Animated fill */}
                <defs>
                    <clipPath id={`star-clip-${reportId}`}>
                        <rect 
                            x="0" 
                            y="0" 
                            width="24" 
                            height="24"
                            style={{
                                transform: `translateY(${100 - fillProgress}%)`,
                                transition: 'none'
                            }}
                        />
                    </clipPath>
                </defs>
                
                <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    fill={starColor}
                    clipPath={`url(#star-clip-${reportId})`}
                />
            </svg>
        </Box>
    );
};

export default StarIcon;
